import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { useReserveSlot } from '@hooks/useReserveSlot';
import { useCheckoutStore } from '@store/checkoutStore';
import type {
  BookingSlotSearchResponse,
  InstallationFormValues,
  PickupAddress,
  SlotReservationRequest,
} from '@t/checkout.types';

interface UseSaveInstallationInfoParams {
  sessionId: string | null;
  slotsData: BookingSlotSearchResponse | null;
  pickupAddress: PickupAddress | null;
}

const buildPayload = (
  values: InstallationFormValues,
  slotsData: BookingSlotSearchResponse | null,
  pickupAddress: PickupAddress | null
): SlotReservationRequest => {
  const isSelf = values.installationMethod === 'SELF';
  const isPickup = isSelf && values.hardwareDelivery === 'PICKUP';
  const rawNotes = values.specialInstructions || values.deliveryInstructions;

  return {
    startDateTime: values.selectedTimeSlot!.startTime,
    endDateTime: values.selectedTimeSlot!.endTime,
    installationMethod: values.installationMethod,
    issueTypeId: slotsData?.issueTypeId ?? undefined,
    timezone: slotsData?.timezone ?? undefined,
    notes: rawNotes || undefined,
    requestedDate: values.selectedDate ? values.selectedDate.toISOString().slice(0, 10) : undefined,
    deliveryMethod: isSelf ? (isPickup ? 'PICKUP' : 'DROPOFF') : undefined,
    pickupAddress: isPickup && pickupAddress ? pickupAddress : undefined,
    dynamicParameters: slotsData?.dynamicParameters,
  };
};

export const useSaveInstallationInfo = ({ sessionId, slotsData, pickupAddress }: UseSaveInstallationInfoParams) => {
  const queryClient = useQueryClient();
  const setInstallationInfo = useCheckoutStore((s) => s.setInstallationInfo);
  const completeStep = useCheckoutStore((s) => s.completeStep);

  const { reserve, isPending } = useReserveSlot(sessionId ?? '', {
    onError: (error) => {
      const message =
        error.name === 'SLOT_NOT_AVAILABLE'
          ? 'Someone else has already booked this slot. Please select another option.'
          : 'Failed to save installation details. Please try again.';
      toast.error(message);
    },
  });

  const save = (values: InstallationFormValues): void => {
    if (!values.selectedTimeSlot) return;

    reserve(buildPayload(values, slotsData, pickupAddress), {
      onSuccess: () => {
        setInstallationInfo(values);
        completeStep('installation');
        queryClient.invalidateQueries({ queryKey: ['shoppingCart', sessionId] });
      },
    });
  };

  return {
    save,
    isSaving: isPending,
  };
};
